// Shopping Cost Calculator - Shopper's Ledger (Newspaper Theme)
import { t, Language } from '../translations';

export interface ShoppingItem {
    key: string;
    label: string;
    labelMm?: string;
    unit: string;
    price: number;
    quantity: number;
}

export interface ShoppingCalculatorState {
    items: ShoppingItem[];
    isOpen: boolean;
}

const STORAGE_KEY = 'myanmar-prices-shopping-list';

/**
 * Load saved shopping list from localStorage
 */
export function loadShoppingList(): ShoppingItem[] {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : [];
    } catch {
        return [];
    }
}

/**
 * Save shopping list to localStorage
 */
export function saveShoppingList(items: ShoppingItem[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
        console.warn('Failed to save shopping list');
    }
}

/**
 * Calculate total cost of shopping list
 */
export function calculateTotal(items: ShoppingItem[]): number {
    return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
}

/**
 * Format currency for display
 */
function formatCurrency(value: number): string {
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 }) + ' Ks';
}

/**
 * Render the Shopper's Ledger drawer (newspaper style)
 */
export function renderShoppingCalculator(
    items: ShoppingItem[],
    isOpen: boolean,
    lang: Language = 'en', // Add language prop
    onRemove: (key: string) => void,
    onUpdateQuantity: (key: string, quantity: number) => void,
    onClear: () => void,
    onToggle: () => void
): string {
    const total = calculateTotal(items);

    // We don't need itemCount here if we don't display it in the drawer header directly, 
    // but we can keep it for future use.

    const itemsHTML = items.length === 0
        ? `<div class="empty-state">
            <p>${t('emptyLedger', lang)}</p>
            <p style="font-size: 0.85rem; margin-top: 0.5rem;">${t('emptyHint', lang)}</p>
           </div>`
        : items.map(item => {
            const displayLabel = (lang === 'mm' && item.labelMm) ? item.labelMm : item.label;
            return `
            <div class="ledger-item" data-key="${item.key}">
                <div class="ledger-item-info">
                    <span class="ledger-item-name">${displayLabel}</span>
                    <span class="ledger-item-unit">${formatCurrency(item.price)} per unit</span>
                </div>
                <div class="ledger-item-controls">
                    <button class="qty-btn" data-action="decrease" data-key="${item.key}">−</button>
                    <span class="qty-display" data-key="${item.key}">${item.quantity}</span>
                    <button class="qty-btn" data-action="increase" data-key="${item.key}">+</button>
                </div>
                <span class="ledger-item-price">${formatCurrency(item.price * item.quantity)}</span>
                <button class="remove-btn" data-key="${item.key}" title="Remove">×</button>
            </div>
        `}).join('');

    return `
        <div class="shopping-drawer">
            <div class="drawer-header">
                <h3 class="drawer-title">${t('ledgerTitle', lang)}</h3>
                <button class="close-btn" id="calculator-close" title="${t('close', lang)}">×</button>
            </div>
            
            <div class="drawer-content">
                ${itemsHTML}
            </div>
            
            <div class="drawer-footer">
                <div class="total-row">
                    <span class="total-label">${t('estimatedTotal', lang)}</span>
                    <span class="total-value">${formatCurrency(total)}</span>
                </div>
                ${items.length > 0 ? `
                    <button class="clear-btn" id="calculator-clear">
                        ${t('clearLedger', lang)}
                    </button>
                ` : ''}
            </div>
        </div>
    `;
}

/**
 * Initialize calculator event listeners
 */
export function initCalculatorEvents(
    container: HTMLElement,
    onRemove: (key: string) => void,
    onUpdateQuantity: (key: string, quantity: number) => void,
    onClear: () => void,
    onToggle: () => void
): void {
    // Close button
    container.querySelector('#calculator-close')?.addEventListener('click', onToggle);

    // Backdrop click to close
    container.addEventListener('click', (e) => {
        if (e.target === container) {
            onToggle();
        }
    });

    // Clear button
    container.querySelector('#calculator-clear')?.addEventListener('click', onClear);

    // Remove buttons
    container.querySelectorAll('.remove-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const key = (e.currentTarget as HTMLElement).dataset.key;
            if (key) onRemove(key);
        });
    });

    // Quantity decrease buttons
    container.querySelectorAll('.qty-btn[data-action="decrease"]').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const key = (e.currentTarget as HTMLElement).dataset.key;
            const display = container.querySelector(`.qty-display[data-key="${key}"]`) as HTMLElement;
            if (key && display) {
                const newQty = Math.max(1, parseInt(display.textContent || '1') - 1);
                onUpdateQuantity(key, newQty);
            }
        });
    });

    // Quantity increase buttons
    container.querySelectorAll('.qty-btn[data-action="increase"]').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const key = (e.currentTarget as HTMLElement).dataset.key;
            const display = container.querySelector(`.qty-display[data-key="${key}"]`) as HTMLElement;
            if (key && display) {
                const newQty = Math.min(99, parseInt(display.textContent || '1') + 1);
                onUpdateQuantity(key, newQty);
            }
        });
    });
}
